import { useState } from 'react'

import type { ReportRequest } from '~/widgets/CostBomDemo/components/HeroComposer'

import { HeroSourcingSelect } from '~/widgets/CostBomDemo/components/HeroSourcingSelect'
import {
  isEmailAddress,
  useCostModelLead,
} from '~/widgets/CostBomDemo/hooks/useCostModelLead'
import {
  CALENDLY_URL,
  COST_BOM_DEMO_STRINGS,
} from '~/widgets/CostBomDemo/strings'

interface HeroReportAskProps {
  request: ReportRequest
  /** Lets the card around this one drop its dismiss once the ask is answered. */
  onSent?: () => void
}

/**
 * The email ask: a recap of what they sent, and a work address to send the
 * model to. An accepted address goes to the lead tracker with the description
 * and sourcing, for someone to build the model from.
 *
 * Send works with an empty composer, so a card opened without a product asks
 * for it here instead: product and sourcing become fields, and the product is
 * required to submit. The card stays in that mode while open rather than
 * switching to the recap mid-edit.
 */
export function HeroReportAsk({
  request,
  onSent = undefined,
}: HeroReportAskProps) {
  const [editable] = useState(request.product === '')
  const [product, setProduct] = useState(request.product)
  const [sourcing, setSourcing] = useState(request.sourcing)
  const [productError, setProductError] = useState(false)
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const { submitLead } = useCostModelLead()

  const submit = () => {
    const productMissing = product.trim() === ''
    const emailMalformed = !isEmailAddress(email)
    setProductError(productMissing)
    setError(emailMalformed ? COST_BOM_DEMO_STRINGS.emailInvalid : null)
    if (productMissing || emailMalformed) return

    const result = submitLead({
      email: email.trim(),
      product: product.trim(),
      country: sourcing,
      source: 'hero-report',
    })
    if (!result.ok) {
      setError(result.error)
      return
    }

    setSent(true)
    onSent?.()
  }

  if (sent) {
    return (
      <div className="hero-report hero-report--sent">
        <p className="hero-report__sent-title">
          {COST_BOM_DEMO_STRINGS.sentTitle}
        </p>
        <p className="hero-report__sent-body">
          {COST_BOM_DEMO_STRINGS.sentBody} {email.trim()}.
        </p>
        <p className="hero-report__sent-offer">
          {COST_BOM_DEMO_STRINGS.sentExpertLede}
        </p>
        <a
          className="hero-report__cta"
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {COST_BOM_DEMO_STRINGS.talkToExpert}
        </a>
      </div>
    )
  }

  return (
    <div className="hero-report">
      <p className="hero-report__title">{COST_BOM_DEMO_STRINGS.reportTitle}</p>
      <p className="hero-report__lede">{COST_BOM_DEMO_STRINGS.reportLede}</p>

      {editable ? (
        <div className="hero-report-recap hero-report-recap--edit">
          <div className="hero-report-recap__row">
            <label
              className="hero-report-recap__label"
              htmlFor="hero-report-product"
            >
              {COST_BOM_DEMO_STRINGS.recapProduct}
            </label>
            <div className="hero-report-recap__value">
              <input
                id="hero-report-product"
                className="hero-report__input hero-report-recap__input"
                value={product}
                placeholder={COST_BOM_DEMO_STRINGS.composerPlaceholder}
                aria-invalid={productError}
                onChange={(e) => {
                  setProduct(e.target.value)
                  setProductError(false)
                }}
              />
              {productError && (
                <p className="hero-report__error">
                  {COST_BOM_DEMO_STRINGS.composerNeedsProduct}
                </p>
              )}
            </div>
          </div>
          <div className="hero-report-recap__row">
            <span className="hero-report-recap__label">
              {COST_BOM_DEMO_STRINGS.composerSourcing}
            </span>
            <div className="hero-report-recap__value">
              <HeroSourcingSelect
                value={sourcing}
                onChange={setSourcing}
                showLabel={false}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="hero-report-recap">
          <p className="hero-report-recap__row">
            <span className="hero-report-recap__label">
              {COST_BOM_DEMO_STRINGS.recapProduct}
            </span>
            <span className="hero-report-recap__value">{request.product}</span>
          </p>
          <p className="hero-report-recap__row">
            <span className="hero-report-recap__label">
              {COST_BOM_DEMO_STRINGS.composerSourcing}
            </span>
            <span className="hero-report-recap__value">{request.sourcing}</span>
          </p>
        </div>
      )}

      <label className="hero-report__label" htmlFor="hero-report-email">
        {COST_BOM_DEMO_STRINGS.emailLabel}
        <input
          id="hero-report-email"
          className="hero-report__input"
          type="email"
          value={email}
          placeholder={COST_BOM_DEMO_STRINGS.emailPlaceholder}
          onChange={(e) => {
            setEmail(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
        />
      </label>
      {error && <p className="hero-report__error">{error}</p>}
      <button type="button" className="hero-report__cta" onClick={submit}>
        {COST_BOM_DEMO_STRINGS.reportSend}
      </button>
    </div>
  )
}
